import { Edge, Graph, Node, useGraphInstance, useGraphStore, useKeyboard } from "@antv/xflow"
import { useEdgeEvents } from "./useEdgeEvents"
import { useNodeEvents } from "./useNodeEvents"
import { useCellEvents } from "./useCellEvents"
import { LineEnd, LineEndEnum, NodeShapes } from "../services/modal"
import { EdgeSides, LineType } from "../constant"
import { getNodePortPosition } from "./helper"
import { getLineEndType } from "../utils"
import { useHasPermissions } from "@/components/OPermission/OPermission"
import { ROLE_DRAFTER } from "@/components/OPermission/PlanPermission"

interface NodeAndEdge {
  edges: Edge[], nodes: Node[]
}

export const useEditorEvents = (drawingStream = false, isTemplate = false) => {
  const graph = useGraphInstance()
  const hasPermission = useHasPermissions();
  const {removeEdges, removeNodes} = useGraphStore(state => state)
  useNodeEvents()
  useEdgeEvents(drawingStream, isTemplate)
  useCellEvents()

  useKeyboard(['backspace', 'delete'], () => {
    if (!graph) return;
    if (!hasPermission(ROLE_DRAFTER)) {
      return;
    }
    const result: NodeAndEdge = graph.getSelectedCells().reduce((res: any, cur) => {
      if (graph.isEdge(cur)) {
        res.edges.push(cur);
      }
      if (graph.isNode(cur)) {
        res.nodes.push(cur)
      }
      return res
    }, { edges: [], nodes: [] } as NodeAndEdge)
    const {edges, nodes} = result;
    if (edges.length > 0) {
      edges.forEach(edge => {
        const data = edge.getData();
        if (data.type !== LineType.FlowLine) {
          graph.removeEdge(edge.id)
          return;
        }
        const {nodes, lines} = deepFindStreamLines(edge, graph)
        removeEdges(lines)
        removeNodes(nodes)
      })
    }
    if (nodes.length > 0) {
      nodes.forEach(item => {
        const incomings = graph.getIncomingEdges(item) || []
        const outgoings = graph.getOutgoingEdges(item) || []
        if (item.shape === NodeShapes.LineJump) {
          const incomeLine = incomings[0]
          const outcomeLine = outgoings[0]
          if (!incomeLine || !outcomeLine) {
            // not valid, so delete them.
            graph.removeCell(item)
            incomeLine && graph.removeCell(incomeLine);
            outcomeLine && graph.removeCell(outcomeLine);
            return;
          }
          const inPoints = incomeLine.getVertices();
          const outPoints = outcomeLine.getVertices();
          const allPoints = [...inPoints, ...outPoints]
          if (isOriginStream(outcomeLine)) {
            outcomeLine.setVertices(allPoints)
            outcomeLine.setSource(incomeLine.getSource())
            graph.removeCell(incomeLine)
          } else {
            incomeLine.setVertices(allPoints)
            incomeLine.setTarget(outcomeLine.getTarget())
            graph.removeCell(outcomeLine)
          }
          graph.removeCell(item)
          return;
        }
        if ([...incomings, ...outgoings].length === 0) {
          graph.removeCell(item)
          return;
        }
        incomings.forEach(line => {
          changeLineEnd(line, item, EdgeSides.Target)
        })

        outgoings.forEach(line => {
          changeLineEnd(line, item, EdgeSides.Source)
        })
        graph.removeCell(item)
      })
    }
  });
}

export function deepFindStreamLines(edge: Edge, graph: Graph, init = {lines: [] as string[], nodes: [] as string[]}): {lines: string[], nodes: string[]} {
  const source = edge.getSource() as any;
  const target = edge.getTarget() as any;
  const result = [source, target].filter(item => getLineEndType(item) === LineEndEnum.LinkToPort)
  .filter(item => !init.nodes.includes(item.cell))
  .reduce((res, current) => {
    const cell = graph.getCellById(current.cell)
    if (cell && cell.shape === NodeShapes.LineJump) {
      res.nodes.push(cell.id)
      graph.getConnectedEdges(cell).filter(edgeItem => edgeItem.id !== edge.id)
        .forEach(edgeItem => {
          const childRes = deepFindStreamLines(edgeItem, graph, init)
          res.lines.push(...childRes.lines);
          res.nodes.push(...childRes.nodes);
        })
      return res;
    }
    res.lines.push(edge.id)
    return res;
  }, init)
  result.lines.push(edge.id)

  return {
    lines: Array.from(new Set(result.lines)), nodes: Array.from(new Set(result.nodes))
  }
}

function isOriginStream(line: Edge) {
  const data = line.getData();
  if (!data.streamId) return true;
  if (data.streamId === line.id) return true

  return false;
}

function changeLineEnd(line: Edge, node: Node, type: EdgeSides) {
  const end = type === EdgeSides.Source ? line.getSource() : line.getTarget()
  const {port} = end as any
  const pos = getNodePortPosition(node, port)
  if (pos) {
    const seter = type === EdgeSides.Source ? line.setSource : line.setTarget
    seter.apply(line, [pos])
  }
}